import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form, Tooltip } from "react-bootstrap";
import { Button, ButtonType } from "../../../Components/Button/Button";
import { OverlayTrigger } from "../../../Components/OverlayTrigger";
import { Spinner } from "../../../Components/Spinner";
import { ButtonFrameworkVariant } from "../../../Components/ButtonFramework/ButtonFramework";
import { useSetError } from "../../../redux/modals";
import { awaitJSON, MetaLambdaFetch } from "../../../utils/fetch-utils";
import * as R from "ramda";
import {
  SEARCH_DELAY,
  DEFAULT_SEARCH_RESPONSE,
  DROPDOWN_BTN,
  SEARCH_MENU,
} from "../../MetaBuyingConstants";
import { TargetingType } from "@blisspointmedia/bpm-types/dist/MetaBuying";

interface TargetingSearchMenuProps {
  placeholder: string;
  menuId: string;
  closeMenu: () => void;
  showMenu: boolean;
  applySelections: (value: Record<string, any>[]) => void;
  appliedSelections: Record<string, any>[];
  business_name: string;
  targetType: TargetingType;
  title?: string;
}

export const TargetingSearchMenu = ({
  placeholder,
  menuId,
  closeMenu,
  showMenu,
  applySelections,
  appliedSelections,
  business_name,
  targetType,
  title,
}: TargetingSearchMenuProps): JSX.Element => {
  const setError = useSetError();

  const [searchQuery, setSearchQuery] = useState("");
  const [prevSearchQuery, setPrevSearchQuery] = useState("");
  const [selectedTargets, setSelectedTargets] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [searchResponse, setSearchResponse] = useState<{
    query: string;
    data: Record<string, any>[];
  }>(DEFAULT_SEARCH_RESPONSE);

  const dropdownBtnId = [menuId, DROPDOWN_BTN].join("_");
  const searchMenuId = [menuId, SEARCH_MENU].join("_");

  useEffect(() => {
    setSelectedTargets(R.clone(appliedSelections));
  }, [showMenu, appliedSelections]);

  const searchFunction = useCallback(
    async (query: string) => {
      let params: {
        business_name: string;
        targetType: TargetingType;
        query: string;
      } = {
        business_name,
        targetType,
        query,
      };
      const res = await MetaLambdaFetch("/searchMetaDetailedTargeting", {
        params,
      });
      const locations = await awaitJSON(res);
      return locations;
    },
    [business_name, targetType]
  );

  const querySearchFunction = useCallback(
    (query: string) => {
      setLoading(true);
      setTimeoutId(
        setTimeout(() => {
          (async () => {
            try {
              let { data } = await searchFunction(query);
              setSearchResponse({ query, data });
            } catch (e) {
              setError({
                message: e instanceof Error ? e.message : String(e),
                reportError: e,
              });
              setLoading(false);
            }
          })();
        }, SEARCH_DELAY)
      );
    },
    [searchFunction, setError]
  );

  useEffect(() => {
    const { query, data } = searchResponse;
    if (searchQuery === query && searchQuery.length) {
      setData(data);
      setSearchResponse(DEFAULT_SEARCH_RESPONSE);
      setLoading(false);
    }
  }, [searchResponse, searchQuery]);

  useEffect(() => {
    if (searchQuery !== prevSearchQuery) {
      setPrevSearchQuery(searchQuery);
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      if (searchQuery.length) {
        querySearchFunction(searchQuery);
      } else {
        setData([]);
        setLoading(false);
      }
    }
  }, [searchQuery, prevSearchQuery, querySearchFunction, timeoutId]);

  useEffect(() => {
    if (!showMenu) {
      setSearchQuery("");
      setData([]);
      setLoading(false);
      setSearchResponse(DEFAULT_SEARCH_RESPONSE);
    }
  }, [showMenu]);

  const selectedIds: string[] = useMemo(() => selectedTargets.map(({ id }) => id), [
    selectedTargets,
  ]);

  return (
    <div id={dropdownBtnId} className="metaBuyingSearchMenu menuToggle">
      <div className="title">{title}</div>
      <Button className="dropdown-toggle" type={ButtonType.OUTLINED}>
        {appliedSelections.length > 0 ? (
          appliedSelections.map(({ name }, index) => (
            <div
              key={index}
              className="appliedSelection"
              onClick={() => {
                const newAppliedSelections = R.remove(index, 1, appliedSelections);
                applySelections(newAppliedSelections);
                setSelectedTargets(newAppliedSelections);
                closeMenu();
              }}
            >
              <span>{name}</span>
            </div>
          ))
        ) : (
          <span className="searchMenuPlaceholder">{placeholder}</span>
        )}
      </Button>
      {showMenu && (
        <div id={searchMenuId} className="searchMenu menuToggle">
          {" "}
          <div>
            <Form.Control
              size="sm"
              className="searchBar"
              placeholder="Search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div className="searchMenuRows">
              {loading ? (
                <Spinner size={50} />
              ) : (
                data.map((option, index) => (
                  <OverlayTrigger
                    key={index}
                    placement="right center"
                    overlay={
                      (option.coverage_lower_bound && option.coverage_upper_bound) ||
                      (option.audience_size_lower_bound && option.audience_size_upper_bound) ? (
                        <Tooltip id={`id:${menuId}-row-${index}`}>
                          {option.description && (
                            <>
                              {option.description}
                              <br />
                            </>
                          )}
                          {`Size: ${(
                            option.coverage_lower_bound || option.audience_size_lower_bound
                          ).toLocaleString()} - ${(
                            option.coverage_upper_bound || option.audience_size_upper_bound
                          ).toLocaleString()}`}
                        </Tooltip>
                      ) : (
                        <div />
                      )
                    }
                  >
                    <div className="searchMenuRow">
                      <Form.Check
                        className="searchMenuOption"
                        key={index}
                        type="checkbox"
                        label={
                          <div>
                            {option.name}
                            {option.subtext && <span className="sublabel">{option.subtext}</span>}
                            {option.topic && <span className="sublabel">{option.topic}</span>}
                          </div>
                        }
                        checked={R.includes(option.id, selectedIds)}
                        id={option.name}
                        onChange={() =>
                          setSelectedTargets(
                            R.includes(option.id, selectedIds)
                              ? selectedTargets.filter(({ id }) => id !== option.id)
                              : [...selectedTargets, option]
                          )
                        }
                      />
                    </div>
                  </OverlayTrigger>
                ))
              )}
            </div>
            <div className="searchMenuControls">
              <Button
                type={ButtonType.OUTLINED}
                variant={ButtonFrameworkVariant.NO_ICON}
                onClick={closeMenu}
              >
                Cancel
              </Button>
              <Button
                disabled={loading || R.isEmpty(selectedIds)}
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.NO_ICON}
                onClick={() => {
                  applySelections(selectedTargets);
                  closeMenu();
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
